import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Award, Lock, Trophy, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge context for global state
const BadgeContext = createContext(null);

export const useBadges = () => {
  const context = useContext(BadgeContext);
  if (!context) {
    throw new Error('useBadges must be used within a BadgeProvider');
  }
  return context;
};

export function BadgeProvider({ children }) {
  const [userBadges, setUserBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [newBadgeQueue, setNewBadgeQueue] = useState([]);
  const [showingBadge, setShowingBadge] = useState(null);

  const fetchAllBadges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/badges`);
      setAllBadges(response.data);
    } catch (error) {
      // Silently fail
    }
  }, []);

  const fetchUserBadges = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/badges/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserBadges(response.data.badges || []);
      setUserStats(response.data.stats || {});
    } catch (error) {
      // Silently fail
    }
  }, []);

  const trackAction = useCallback(async (action, params = {}) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      let response;
      if (action === 'layout_change') {
        response = await axios.post(`${API}/badges/track/layout/${params.layout}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/badges/track/${action}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Check for new badges
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        setNewBadgeQueue(prev => [...prev, ...response.data.new_badges]);
        // Refresh user badges
        fetchUserBadges();
      }
    } catch (error) {
      // Silently fail
    }
  }, [fetchUserBadges]);

  // Process badge queue - show one at a time
  useEffect(() => {
    if (newBadgeQueue.length > 0 && !showingBadge) {
      const badge = newBadgeQueue[0];
      setShowingBadge(badge);
      setNewBadgeQueue(prev => prev.slice(1));
    }
  }, [newBadgeQueue, showingBadge]);

  const dismissBadge = () => {
    setShowingBadge(null);
  };

  // Add badge locally (when server confirms)
  const addBadge = useCallback((badge) => {
    setUserBadges(prev => {
      if (prev.some(b => b.id === badge.id)) return prev;
      return [...prev, badge];
    });
    setNewBadgeQueue(prev => [...prev, badge]);
  }, []);

  useEffect(() => {
    fetchAllBadges();
    fetchUserBadges();
  }, [fetchAllBadges, fetchUserBadges]);

  return (
    <BadgeContext.Provider value={{ 
      userBadges, 
      allBadges, 
      userStats,
      trackAction, 
      addBadge, 
      fetchUserBadges,
      hasBadge: (id) => userBadges.some(b => b.id === id)
    }}>
      {children}
      {showingBadge && (
        <BadgeEarnedModal badge={showingBadge} onClose={dismissBadge} />
      )}
    </BadgeContext.Provider>
  );
}

// Modal shown when badge is earned
function BadgeEarnedModal({ badge, onClose }) {
  useEffect(() => {
    // Play celebration sound effect (optional)
    // Auto close after 5 seconds
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md text-center">
        <div className="py-6">
          {/* Confetti animation */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  backgroundColor: ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'][Math.floor(Math.random() * 5)]
                }}
              />
            ))}
          </div>
          
          {/* Badge icon */}
          <div className="relative mx-auto w-24 h-24 mb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-pulse" />
            <div className="absolute inset-2 bg-background rounded-full flex items-center justify-center">
              <span className="text-5xl">{badge.icon}</span>
            </div>
            <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-500 animate-bounce" />
          </div>
          
          <h2 className="text-2xl font-bold font-serif mb-2">Badge Earned!</h2>
          <h3 className="text-xl font-semibold text-primary mb-2">{badge.name}</h3>
          <p className="text-muted-foreground mb-6">{badge.description}</p>
          
          <Button onClick={onClose} className="rounded-full px-8">
            <Trophy className="w-4 h-4 mr-2" />
            Awesome!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Badge display component for profile/dashboard
export function BadgeDisplay({ badge, locked = false, size = 'md' }) {
  const sizeClasses = {
    sm: 'w-10 h-10 text-xl',
    md: 'w-14 h-14 text-2xl',
    lg: 'w-20 h-20 text-4xl'
  };

  return (
    <div className={`relative group ${locked ? 'opacity-50' : ''}`}>
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${
        locked 
          ? 'bg-muted' 
          : 'bg-gradient-to-br from-yellow-400/20 to-orange-500/20 border-2 border-yellow-500/30'
      } transition-transform group-hover:scale-110`}>
        {locked ? (
          <Lock className="w-1/2 h-1/2 text-muted-foreground" />
        ) : (
          <span>{badge.icon}</span>
        )}
      </div>
      
      {/* Tooltip - positioned BELOW the badge to prevent clipping in modal */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        <div className="bg-popover text-popover-foreground rounded-lg shadow-lg p-3 text-center min-w-[150px] border border-border">
          <p className="font-semibold text-sm">{badge.name}</p>
          <p className="text-xs text-muted-foreground">{badge.description}</p>
          {!locked && badge.earned_at && (
            <p className="text-xs text-primary mt-1">
              Earned {new Date(badge.earned_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Badge showcase grid
export function BadgeShowcase({ showLocked = true }) {
  const { userBadges, allBadges } = useBadges();
  
  const earnedIds = userBadges.map(b => b.id);
  const sortedBadges = [
    ...userBadges,
    ...(showLocked ? allBadges.filter(b => !earnedIds.includes(b.id)) : [])
  ];

  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
      {sortedBadges.map(badge => (
        <BadgeDisplay 
          key={badge.id} 
          badge={badge} 
          locked={!earnedIds.includes(badge.id)} 
        />
      ))}
    </div>
  );
}

// Mini badge row for header/profile
export function BadgeRow({ limit = 5 }) {
  const { userBadges } = useBadges();
  const displayBadges = userBadges.slice(0, limit);
  const remaining = userBadges.length - limit;

  if (userBadges.length === 0) return null;

  return (
    <div className="flex items-center gap-1">
      {displayBadges.map(badge => (
        <BadgeDisplay key={badge.id} badge={badge} size="sm" />
      ))}
      {remaining > 0 && (
        <span className="text-sm text-muted-foreground ml-1">+{remaining}</span>
      )}
    </div>
  );
}

// Full badges page/dialog
export function BadgesDialog({ open, onOpenChange }) {
  const { userBadges, allBadges, userStats } = useBadges();
  const earnedCount = userBadges.length;
  const totalCount = allBadges.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <Award className="w-6 h-6 text-yellow-500" />
            My Badges
            <span className="text-sm font-normal text-muted-foreground ml-2">
              {earnedCount}/{totalCount}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 py-4">
            {/* Earned badges */}
            {userBadges.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">EARNED</h3>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
                  {userBadges.map(badge => (
                    <BadgeDisplay key={badge.id} badge={badge} size="lg" />
                  ))}
                </div>
              </div>
            )}
            
            {/* Locked badges */}
            {allBadges.filter(b => !userBadges.some(ub => ub.id === b.id)).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">LOCKED</h3>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-4">
                  {allBadges
                    .filter(b => !userBadges.some(ub => ub.id === b.id))
                    .map(badge => (
                      <BadgeDisplay key={badge.id} badge={badge} size="lg" locked />
                    ))}
                </div>
              </div>
            )}
            
            {/* Stats */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">YOUR STATS</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatCard label="Mind Maps" value={userStats.mindmaps_created || 0} />
                <StatCard label="Notes" value={userStats.notes_created || 0} />
                <StatCard label="Quizzes" value={userStats.quizzes_completed || 0} />
                <StatCard label="Shares" value={userStats.shares || 0} />
                <StatCard label="Chat Messages" value={userStats.chat_messages || 0} />
                <StatCard label="AI Explanations" value={userStats.ai_explanations || 0} />
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-muted/50 rounded-lg p-3 text-center">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// Add confetti animation to global CSS
export const badgeStyles = `
@keyframes confetti {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(400px) rotate(720deg);
    opacity: 0;
  }
}

.animate-confetti {
  width: 10px;
  height: 10px;
  position: absolute;
  top: -10px;
  animation: confetti 2s ease-out forwards;
}
`;

export default BadgeDisplay;
